<template>
    <section
        class="slice slice-rich-text slice-rich-text--default"
        :class="{'slice-rich-text--full-width': slice.primary.fullWidth}"
        data-testid="rich-text-default-slice"
    >
        <div class="slice-content" data-testid="rich-text-default-content">
            <MktRichContent :content="contentChunks" />
        </div>
    </section>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import type {RichContentChunk} from '@nuxt-marketing-module/shared/types/RichContent';
    import type {RTNode} from '@prismicio/types';
    import {getSliceComponentProps} from '@prismicio/vue';

    import type {RichTextSlice, RichTextSliceDefault} from '@prismicio-types';
    import {MktRichContent} from '#components';

    const props = defineProps(getSliceComponentProps<RichTextSlice>());
    const slice = computed(() => props.slice as RichTextSliceDefault);

    function isTitleRTNode(rtNode: RTNode) {
        return rtNode.type === 'heading2' || rtNode.type === 'heading3';
    }

    const contentChunks = computed(() =>
        (slice.value.primary.content as RTNode[]).reduce<RichContentChunk[]>((acc: RichContentChunk[], current: RTNode) => {
            if (isTitleRTNode(current)) {
                acc.push({type: 'title', content: [current]});
            } else {
                const lastChunk = acc[acc.length - 1];
                if (!lastChunk || lastChunk.type === 'title') {
                    acc.push({type: 'content', content: [current]});
                } else {
                    lastChunk.content.push(current as never);
                }
            }

            return acc;
        }, []),
    );
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    $max-width: 840px;

    .slice-rich-text--default {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        margin: 0 auto;

        .slice-content {
            text-align: left;
        }

        &:not(.slice-rich-text--full-width) .slice-content {
            max-width: $max-width;
            padding-left: 0;
            padding-right: 0;
        }

        h1,
        h2,
        h3,
        h4,
        h5,
        h6 {
            font-family: var(--joy-font-family-title);
            line-height: var(--joy-line-height-small);
            color: var(--joy-color-neutral-60);
            margin: 0;
        }

        h1 {
            font-weight: var(--joy-font-weight-bold);
            line-height: var(--joy-line-height-small);
            font-size: 40px;

            @include mq.screen_md(min) {
                font-size: 65px;
            }
        }

        h2 {
            font-weight: var(--joy-font-weight-bold);
            font-size: var(--joy-font-size-secondary-400);

            @include mq.screen_md(min) {
                font-size: 40px;
            }
        }

        h3 {
            font-weight: var(--joy-font-weight-normal);
            font-size: var(--joy-font-size-secondary-200);

            @include mq.screen_md(min) {
                font-size: 32px;
            }
        }

        h4 {
            font-weight: var(--joy-font-weight-normal);
            font-size: var(--joy-font-size-primary-800);

            @include mq.screen_md(min) {
                font-size: var(--joy-font-size-secondary-200);
            }
        }

        li,
        ol,
        p,
        ul {
            color: var(--joy-color-text-default);
        }

        p,
        ol,
        ul,
        li {
            font-size: var(--joy-font-size-primary-400);
        }

        h1,
        h2 {
            margin-bottom: var(--joy-core-spacing-8);
        }

        h3,
        h4,
        h5,
        h6,
        p,
        ul,
        ol {
            margin-bottom: var(--joy-core-spacing-7);
        }

        .block-img {
            display: flex;
            justify-content: center;
        }

        img {
            max-width: 100%;
        }

        a {
            color: var(--joy-color-primary-50);
            text-decoration: underline var(--joy-color-primary-50);

            &:hover {
                color: var(--joy-color-primary-70);
                text-decoration: underline var(--joy-color-primary-70);
            }
        }

        div {
            width: 100%;
        }

        div[data-oembed-type='video'] {
            width: 100%;
            padding-top: 56.25%; // 16:9
            position: relative;

            iframe {
                position: absolute;
                inset: 0;
                width: 100%;
                height: 100%;
            }
        }

        .title-anchor {
            position: relative;
            top: -76px;
            visibility: hidden;

            @include mq.screen_md(min) {
                top: -92px;
            }
        }
    }
</style>
